import React, { useState, useEffect } from 'react'
import { Modal, Form, Button, Input, DatePicker, Select } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'

import actions from 'redux/contracts/actions'
import ddl from 'redux/dropdowns/actions'

/**
 * "Copy Project" modal — admin-only. Lets the user pick a source contract and
 * enter the new contract's identifying fields (cost code, contract number,
 * project name, contract title, begin/end date).
 *
 * Only the master data is copied (single transaction, all FK remaps built in).
 * Proposals and work orders are not: they carried ELINs/CLINs of the source
 * project into the new one.
 */
export default () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const intl = useIntl()

  const { contracts, dropdowns } = useSelector(state => ({
    contracts: state.contracts,
    dropdowns: state.dropdowns,
  }))

  const visible = !!(contracts && contracts.copying)
  const sourceOptions = (dropdowns && dropdowns.contracts && dropdowns.contracts.options) || []

  useEffect(() => {
    if (visible) {
      // Pull the full contracts dropdown so the source picker shows every contract,
      // not just the currently paginated ones in the grid.
      dispatch({ type: ddl.FETCH_DROPDOWN, entity: ddl.CONTRACTS })
      form.resetFields()
    }
  }, [visible, dispatch, form])

  // If a copy just finished successfully, close the modal.
  useEffect(() => {
    if (contracts && contracts.copySuccess) {
      setSubmitting(false)
      dispatch({ type: actions.CLOSE_COPY_CONTRACT })
    }
  }, [contracts && contracts.copySuccess, dispatch])

  const onCancel = () => {
    dispatch({ type: actions.CLOSE_COPY_CONTRACT })
  }

  const requiredRule = [
    { required: true, message: <FormattedMessage id="required.field" /> },
  ]

  const onSubmit = () => {
    form
      .validateFields()
      .then(values => {
        const payload = {
          sourceContractID: values.sourceContractID,
          costCode: values.costCode || '',
          contractNumber: values.contractNumber,
          projectNumber: values.projectNumber,
          contractTitle: values.contractTitle || '',
          beginDate: values.beginDate ? values.beginDate.format('YYYY-MM-DD') : null,
          endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
        }
        setSubmitting(true)
        dispatch({ type: actions.COPY_CONTRACT, values: payload })
      })
      .catch(() => {
        // validation errors — ant design highlights them automatically
      })
  }

  return (
    <Modal
      title={<FormattedMessage id="copyProject" />}
      visible={visible}
      footer={null}
      onCancel={onCancel}
      width="600px"
      destroyOnClose
    >
      <Form
        form={form}
        labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
        wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
        labelAlign="left"
      >
        <Form.Item
          name="sourceContractID"
          label={<FormattedMessage id="copyProject.sourceProject" />}
          rules={requiredRule}
        >
          <Select
            showSearch
            placeholder={intl.formatMessage({ id: 'copyProject.sourceProject' })}
            filterOption={(input, option) => {
              const name = (option && option.name) || ''
              const value = String((option && option.value) || '')
              return (
                name.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                value.toLowerCase().indexOf(input.toLowerCase()) >= 0
              )
            }}
          >
            {sourceOptions.map(item => (
              <Select.Option
                key={item.value}
                name={item.key}
                value={Number(item.value)}
              >
                {`${item.key || ''}${item.description ? ` — ${item.description}` : ''}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="costCode" label={<FormattedMessage id="costCode" />}>
          <Input autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="contractNumber"
          label={<FormattedMessage id="contractNumber" />}
          rules={requiredRule}
        >
          <Input autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="projectNumber"
          label={<FormattedMessage id="projectName" />}
          rules={requiredRule}
        >
          <Input autoComplete="off" />
        </Form.Item>

        <Form.Item name="contractTitle" label={<FormattedMessage id="contractTitle" />}>
          <Input autoComplete="off" />
        </Form.Item>

        <Form.Item
          name="beginDate"
          label={<FormattedMessage id="beginDate" />}
          rules={requiredRule}
        >
          <DatePicker autoComplete="off" format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="endDate"
          label={<FormattedMessage id="endDate" />}
          rules={requiredRule}
        >
          <DatePicker autoComplete="off" format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <div className="ant-modal-footer actions" style={{ marginTop: 12 }}>
          <Button onClick={onCancel}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button type="primary" loading={submitting} onClick={onSubmit}>
            <FormattedMessage id="submit" />
          </Button>
        </div>
      </Form>
    </Modal>
  )
}
